// Native (Capacitor) helpers. Everything is a no-op on the web build, so the
// same code runs in the browser and inside the Android app.
import { Capacitor } from "@capacitor/core";
import api from "@/lib/api";

export const isNativeApp = () => {
  try { return Capacitor.isNativePlatform(); } catch { return false; }
};

// Native bundle id. Android and the new public iOS listing both ship as
// nz.co.fieldlink.app.
const appBundleId = () => "nz.co.fieldlink.app";

// Status bar + splash on launch.
export async function initNativeApp() {
  if (!isNativeApp()) return;
  try {
    const { StatusBar, Style } = await import("@capacitor/status-bar");
    // Keep the web view below the status bar / camera notch so the top bar is
    // tappable (by default Capacitor draws the app under the status bar).
    await StatusBar.setOverlaysWebView({ overlay: false }).catch(() => {});
    await StatusBar.setStyle({ style: Style.Dark }).catch(() => {});
    await StatusBar.setBackgroundColor({ color: "#09090b" }).catch(() => {});
  } catch {}
  try {
    const { SplashScreen } = await import("@capacitor/splash-screen");
    await SplashScreen.hide().catch(() => {});
  } catch {}
  installIOSFileShim();
}

const nameFromType = (t) => ({
  "application/pdf": "document.pdf", "text/csv": "export.csv",
  "image/png": "image.png", "image/jpeg": "image.jpg",
}[t] || `file_${Date.now()}`);

// iOS WKWebView can't download blob:/data: URLs or open them in a new tab, which
// is how ~every PDF/CSV export in the app works. Instead of editing dozens of
// call sites, we intercept those two patterns once (iOS only) and reroute them
// through the native share/preview sheet. Web and Android are untouched.
let fileShimInstalled = false;
function installIOSFileShim() {
  if (fileShimInstalled) return;
  if (!(isNativeApp() && Capacitor.getPlatform() === "ios")) return;
  fileShimInstalled = true;

  const origOpen = window.open.bind(window);
  window.open = (url, ...rest) => {
    if (typeof url === "string" && /^(blob:|data:)/i.test(url)) {
      (async () => {
        try {
          const blob = await (await fetch(url)).blob();
          await saveOrOpenFile(blob, nameFromType(blob.type));
        } catch {}
      })();
      return null;
    }
    return origOpen(url, ...rest);
  };

  document.addEventListener("click", (e) => {
    const a = e.target?.closest?.("a[download]");
    if (!a) return;
    const href = a.getAttribute("href") || "";
    if (!/^(blob:|data:)/i.test(href)) return;
    e.preventDefault();
    (async () => {
      try {
        const blob = await (await fetch(href)).blob();
        await saveOrOpenFile(blob, a.getAttribute("download") || nameFromType(blob.type));
      } catch {}
    })();
  }, true);
}

// Register the device for push and send its FCM token to the backend.
export async function registerPushForUser() {
  if (!isNativeApp()) return;
  try {
    const { PushNotifications } = await import("@capacitor/push-notifications");
    let perm = await PushNotifications.checkPermissions();
    if (perm.receive !== "granted") perm = await PushNotifications.requestPermissions();
    if (perm.receive !== "granted") return;
    await PushNotifications.removeAllListeners();
    await PushNotifications.addListener("registration", async (token) => {
      try {
        await api.put("/devices/push-token", {
          token: token.value, platform: Capacitor.getPlatform(), app_id: appBundleId(),
        });
      } catch {}
    });
    await PushNotifications.addListener("registrationError", () => {});
    await PushNotifications.register();
  } catch {}
}

// Capture a photo with the native camera. Returns a File, or null if the
// caller should fall back to a normal file input (web / cancelled).
export async function captureNativePhoto() {
  if (!isNativeApp()) return null;
  try {
    const { Camera, CameraResultType, CameraSource } = await import("@capacitor/camera");
    const photo = await Camera.getPhoto({
      quality: 70, resultType: CameraResultType.Uri, source: CameraSource.Prompt,
      allowEditing: false, saveToGallery: false,
    });
    const res = await fetch(photo.webPath);
    const blob = await res.blob();
    const ext = photo.format || "jpeg";
    return new File([blob], `photo_${Date.now()}.${ext}`, { type: blob.type || `image/${ext}` });
  } catch {
    return null;
  }
}

// Current GPS position via the native plugin (falls back to browser geolocation).
export async function getNativeLocation() {
  if (!isNativeApp()) return null;
  try {
    const { Geolocation } = await import("@capacitor/geolocation");
    let perm = await Geolocation.checkPermissions();
    if (perm.location !== "granted") perm = await Geolocation.requestPermissions();
    if (perm.location !== "granted" && perm.coarseLocation !== "granted") return null;
    const pos = await Geolocation.getCurrentPosition({ enableHighAccuracy: true, timeout: 10000 });
    return { lat: pos.coords.latitude, lng: pos.coords.longitude, accuracy: pos.coords.accuracy };
  } catch {
    return null;
  }
}

const blobToBase64 = (blob) =>
  new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onerror = reject;
    r.onload = () => resolve(String(r.result).split(",")[1] || "");
    r.readAsDataURL(blob);
  });

// View or download a generated file (PDF etc). On web/Android we keep the
// existing anchor/window.open behaviour. iOS WKWebView cannot download blob:
// URLs or open them in a new tab, so there we write the file to the cache and
// hand it to the native share/preview sheet (Quick Look, Files, Mail...).
export async function saveOrOpenFile(blob, filename, { open = true } = {}) {
  const isIOS = isNativeApp() && Capacitor.getPlatform() === "ios";
  if (!isIOS) {
    const url = URL.createObjectURL(blob);
    if (open) {
      const w = window.open(url, "_blank");
      if (!w) {
        const a = document.createElement("a");
        a.href = url; a.download = filename; a.click();
      }
    } else {
      const a = document.createElement("a");
      a.href = url; a.download = filename; a.click();
    }
    setTimeout(() => URL.revokeObjectURL(url), 60000);
    return;
  }
  const { Filesystem, Directory } = await import("@capacitor/filesystem");
  const { Share } = await import("@capacitor/share");
  const data = await blobToBase64(blob);
  const safe = (filename || `file_${Date.now()}`).replace(/[^A-Za-z0-9._-]+/g, "_");
  const written = await Filesystem.writeFile({ path: safe, data, directory: Directory.Cache });
  await Share.share({ title: filename, url: written.uri });
}
